import React, { useContext, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { X, UserPlus, Building2, Briefcase, FileText, Users, Heart, Shield, Award } from "lucide-react";

import { ThemeContext } from "../../../../constant/theme";
import { LanguageContext } from "../../../../constant/LanguageContext";
import { apiFetch, useAuth } from "../../../../context/AuthContext";

import ReusableDropdown, { unifiedControlStyle } from "../../../../components/ReusableDropdown";

import { text as allText } from "../../constants/text";
import {
  getStatusOptions,
  getGenderOptions,
  getMaritalOptions,
  getLaborModeOptions,
  getLastContractTermOptions,
} from "../../constants/dropdownOptions";
import { useAddEmployeeForm } from "../../hooks/useAddEmployeeForm";
import { useWorkingCompanyOptions } from "../../hooks/useWorkingCompanyOptions";

const API_URL = process.env.REACT_APP_BASE_URL;

// ─────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────

const SectionTitle = ({ children, styles }) => (
  <div style={styles.sectionTitle}>{children}</div>
);

const FormRow = ({ children, styles }) => (
  <div style={styles.formRow}>{children}</div>
);

const FormField = ({ label, error, styles, children }) => (
  <div style={styles.formGroup}>
    <label style={styles.formLabel}>{label}</label>
    <div style={{ position: "relative" }}>
      {children}
    </div>
    {error && <span style={styles.errorText}>⚠ {error}</span>}
  </div>
);

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────

const AddEmployeeModal = ({
  styles = {},
  departments = [],
  companies = [],
  loading = false,
  screenSize = "lg",
  onClose = () => {},
  onSubmit = async () => {},
  departmentsLoading = false,
  clientName = "",
}) => {
  const { theme }    = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { hasFeatureAccess } = useAuth();
  const t            = allText[language];
  const canAddDepartment = hasFeatureAccess("departments", "add");

  // provide minimal default styles so modal is visible even if parent styles are missing
  const defaultStyles = {
    modal: {
      position: "fixed",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(0,0,0,0.5)",
      zIndex: 9999,
      backdropFilter: "blur(3px)",
      animation: "fadeIn 300ms ease-out",
    },
    modalContent: {
      position: "relative",
      width: "92%",
      maxWidth: 1000,
      maxHeight: "92vh",
      overflowY: "auto",
      background: theme === "dark" 
        ? "linear-gradient(135deg, #0f172a 0%, #1a2a3a 100%)" 
        : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
      color: theme === "dark" ? "#e6eef8" : "#0f172a",
      borderRadius: 16,
      padding: "32px",
      boxShadow: theme === "dark"
        ? "0 25px 60px rgba(0,0,0,0.8), 0 0 1px rgba(255,255,255,0.1)"
        : "0 20px 50px rgba(0,0,0,0.12), 0 0 1px rgba(0,0,0,0.04)",
      border: theme === "dark" 
        ? "1px solid rgba(255,255,255,0.1)" 
        : "1px solid rgba(148,163,184,0.15)",
      // Hide scrollbar across all browsers
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      WebkitOverflowScrolling: "touch",
      animation: "slideUp 400ms cubic-bezier(0.34, 1.56, 0.64, 1)",
    },
    modalHeader: {
      marginBottom: 32,
      paddingBottom: 16,
      borderBottom: theme === "dark" 
        ? "1px solid rgba(255,255,255,0.08)" 
        : "1px solid rgba(0,0,0,0.08)",
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    modalTitle: {
      margin: 0,
      fontSize: 24,
      fontWeight: 800,
      background: theme === "dark"
        ? "linear-gradient(135deg, #fff 0%, #cbd5e1 100%)"
        : "linear-gradient(135deg, #0f172a 0%, #2e7cba 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      letterSpacing: "-0.5px",
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    modalActions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 12,
      marginTop: 32,
      paddingTop: 20,
      borderTop: theme === "dark" 
        ? "1px solid rgba(255,255,255,0.08)" 
        : "1px solid rgba(0,0,0,0.08)",
    },
    primaryBtn: {
      background: "linear-gradient(135deg, #2e7cba 0%, #2e7cba 100%)",
      color: "#fff",
      padding: "12px 24px",
      borderRadius: 10,
      border: "none",
      display: "inline-flex",
      gap: 8,
      alignItems: "center",
      cursor: "pointer",
      fontWeight: 700,
      fontSize: 14,
      transition: "all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)",
      //boxShadow: "0 4px 15px rgba(59, 130, 246, 0.4)",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    secondaryBtn: {
      background: theme === "dark" 
        ? "rgba(30, 41, 59, 0.8)" 
        : "rgba(241, 245, 249, 0.9)",
      color: theme === "dark" ? "#cbd5e1" : "#475569",
      padding: "12px 24px",
      borderRadius: 10,
      border: theme === "dark" 
        ? "1.5px solid rgba(255,255,255,0.15)" 
        : "1.5px solid rgba(0,0,0,0.12)",
      cursor: "pointer",
      fontWeight: 700,
      fontSize: 14,
      transition: "all 300ms ease",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    formRow: {
      display: "flex",
      gap: 18,
      marginBottom: 18,
      flexWrap: "wrap",
    },
    formGroup: {
      flex: "1 1 calc(50% - 9px)",
      minWidth: 220,
      display: "flex",
      flexDirection: "column",
    },
    formLabel: {
      marginBottom: 10,
      fontSize: 12,
      fontWeight: 700,
      color: theme === "dark" ? "#94a3b8" : "#475569",
      textTransform: "uppercase",
      letterSpacing: "0.6px",
    },
    errorText: {
      color: "#ef4444",
      fontSize: 11,
      marginTop: 6,
      fontWeight: 600,
    },
    sectionTitle: {
      fontSize: 13,
      fontWeight: 800,
      margin: "24px 0 16px 0",
      paddingBottom: 0,
      paddingLeft: 12,
      color: theme === "dark" ? "#60a5fa" : "#2e7cba",
      borderLeft: theme === "dark" 
        ? "3px solid rgba(96, 165, 250, 0.6)" 
        : "3px solid #2e7cba",
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    closeBtn: {
      position: "absolute",
      top: 14,
      right: 14,
      width: 40,
      height: 40,
      borderRadius: 10,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: theme === "dark" ? "rgba(13, 18, 28, 0.15)" : "rgba(255,255,255,0.92)",
      border: theme === "dark" ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
      cursor: "pointer",
      //  boxShadow: theme === "dark" ? "0 6px 18px rgba(2,6,23,0.6)" : "0 8px 20px rgba(2,6,23,0.06)",
      transition: "all 200ms ease",
      zIndex: 20,
    },
  };

  styles = { ...defaultStyles, ...styles };

  const isRtl = language === "ar";

  // Adjust styles for RTL languages: move section border and close button
  if (isRtl) {
    styles.sectionTitle = {
      ...styles.sectionTitle,
      borderRight: styles.sectionTitle.borderLeft,
      borderLeft: undefined,
      paddingRight: styles.sectionTitle.paddingLeft,
      //paddingLeft: undefined,
      flexDirection: "row",
      textAlign: "right",
    };

    styles.closeBtn = {
      ...styles.closeBtn,
      right: "auto",
      left: 14,
    };

    styles.formLabel = {
      ...styles.formLabel,
      textAlign: "right",
    };
  }

  // options
  const statusOptions    = getStatusOptions(t);
  const genderOptions    = getGenderOptions(t);
  const maritalOptions   = getMaritalOptions(t);
  const laborModeOptions = getLaborModeOptions(t);
  const lastContractTermOptions = getLastContractTermOptions(t);

  // form state from hook
  const {
    formData,
    formErrors,
    handleInputChange,
    handleFieldChange,
    validateForm,
    resetForm,
  } = useAddEmployeeForm(language);

  // Gross/Net salary are mutually derived: whichever one the user types into
  // becomes `salaryInputSource` (sent to the backend as the authoritative
  // figure on save); the other field locks and shows a live preview fetched
  // from the server — same formulas the save itself will use, never
  // duplicated in the frontend.
  const salaryPreviewTimer = useRef(null);
  const handleSalaryFieldChange = (name, value) => {
    const source = name === "grossSalary" ? "gross" : "net";
    handleFieldChange(name, value);
    handleFieldChange("salaryInputSource", source);

    if (salaryPreviewTimer.current) clearTimeout(salaryPreviewTimer.current);
    if (value === "" || value === null || isNaN(Number(value))) return;

    salaryPreviewTimer.current = setTimeout(async () => {
      try {
        const response = await apiFetch(`${API_URL}/employees/salary-preview`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            grossSalary: source === "gross" ? value : null,
            netSalary: source === "net" ? value : null,
            salaryInputSource: source,
            insuranceSalary: formData.insuranceSalary || null,
          }),
        });
        const data = await response.json();
        if (data?.success) {
          if (source === "gross") handleFieldChange("salary", data.netSalary ?? "");
          else handleFieldChange("grossSalary", data.grossSalary ?? "");
        }
      } catch (err) {
        console.error("Salary preview failed:", err);
      }
    }, 400);
  };

  // Department options with id+name pairs fetched from the Departments API
  // (the `departments` prop only carries derived name strings — kept as fallback).
  const [departmentOptions, setDepartmentOptions] = useState([]);
  // Text shown inside the department dropdown (the department NAME, while
  // formData.departmentId stores the selected department's id).
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState("");

  // Working-company choices — all client companies for real admins,
  // visibility-scoped companies for promoted admin-view accounts.
  const { companyOptions, companiesLoading } = useWorkingCompanyOptions({
    companies,
  });
  const [companySearchTerm, setCompanySearchTerm] = useState("");

  // Manager-picker dropdown — lightweight employee list fetched on open.
  const [managerOptions, setManagerOptions] = useState([]);
  const [managerSearchTerm, setManagerSearchTerm] = useState("");
  useEffect(() => {
    const client = clientName || localStorage.getItem("clientName") || "";
    if (!client) return;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({ clientName: client });
        const response = await apiFetch(`${API_URL}/employees/manager-options?${params}`);
        const json = await response.json().catch(() => null);
        if (!cancelled && response.ok && Array.isArray(json?.data)) {
          setManagerOptions(json.data);
        }
      } catch (err) {
        console.warn("Could not load manager options:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [clientName]);

  // Read-only Infinity Code format hint — e.g. "RAY-####" — fetched from
  // the shared (non-superadmin-only) config route. Deliberately never a
  // concrete "next code": for random formats there is no predictable next
  // value, and for sequential formats two admins with this form open at
  // once would both be shown the same soon-to-be-wrong number.
  const [codeFormatHint, setCodeFormatHint] = useState("");
  useEffect(() => {
    const client = clientName || localStorage.getItem("clientName") || "";
    if (!client) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await apiFetch(`${API_URL}/api/configurations/${encodeURIComponent(client)}`);
        const json = await response.json().catch(() => null);
        if (!cancelled && response.ok && json?.data?.codeFormat) {
          setCodeFormatHint(json.data.codeFormat);
        }
      } catch (err) {
        // Non-critical — the form works fine without the hint.
        console.warn("Could not load Infinity Code format hint:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [clientName]);

  const departmentsWorkingCompany = formData.workingCompany || "";

  useEffect(() => {
    const client = clientName || localStorage.getItem("clientName") || "";
    if (!client || !departmentsWorkingCompany) return;
    let cancelled = false;

    (async () => {
      try {
        const params = new URLSearchParams({
          clientName: client,
          workingCompany: departmentsWorkingCompany,
        });
        const response = await apiFetch(`${API_URL}/api/departments?${params}`);
        const json = await response.json().catch(() => null);
        if (!response.ok || json?.success === false) {
          throw new Error(json?.message || "Failed to load departments");
        }
        const list = Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json)
          ? json
          : [];
        if (!cancelled) setDepartmentOptions(list);
      } catch (err) {
        console.error("Failed to fetch departments:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clientName, departmentsWorkingCompany]);

  // Department stays locked until a working company is chosen.
  const hasWorkingCompany = Boolean(departmentsWorkingCompany);

  // API-fetched id+name options win; the name-list prop is only a fallback
  // and only once a working company has been selected.
  const departmentItems =
    departmentOptions.length > 0
      ? departmentOptions
      : hasWorkingCompany
      ? departments
      : [];

  // dropdown search terms (local — UI only)
  const [statusSearch,    setStatusSearch]    = useState(
    () => statusOptions.find((s) => s.key === formData.status)?.label || ""
  );
  const [genderSearch,    setGenderSearch]    = useState("");
  const [maritalSearch,   setMaritalSearch]   = useState("");
  const [laborModeSearch, setLaborModeSearch] = useState("");
  const [lastContractTermSearch, setLastContractTermSearch] = useState("");

  const handleCreateDepartment = async (name) => {
    try {
      const client = clientName || localStorage.getItem("clientName") || "";
      const response = await apiFetch(`${API_URL}/api/departments`, {
        method: "POST",
        body: JSON.stringify({ clientName: client, workingCompany: formData.workingCompany, name }),
      });
      const json = await response.json().catch(() => null);
      if (!response.ok || json?.success === false) {
        throw new Error(json?.message || "Failed to create department");
      }
      const created = json?.data ?? json;
      setDepartmentOptions((prev) => [...prev, created]);
      return created;
    } catch (err) {
      console.error("Failed to create department:", err);
      window.alert(err.message || (isRtl ? "تعذر إنشاء القسم" : "Failed to create department"));
      return null;
    }
  };

  // ── Handlers ──
  const handleModalClick = (e) => {
    // click outside modal content should close
    if (e.target === e.currentTarget) {
      console.log("AddEmployeeModal: backdrop click -> closing");
      onClose();
    }
  };

  const handleFormKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const tag = (e.target?.tagName || "").toLowerCase();
    if (tag === "button" || e.target?.type === "submit") return;
    if (e.target?.closest?.("[data-reusable-dropdown]")) return;
    e.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    await onSubmit(formData);
    resetForm();
    setDepartmentSearchTerm("");
    setCompanySearchTerm("");
  };

  // ── Shared input style ──
  const baseInputStyle = unifiedControlStyle(theme, isRtl);
  const inputStyle = {
    ...baseInputStyle,
    padding: "12px 14px",
    fontSize: 14,
    borderRadius: 10,
    border: theme === "dark"
      ? "1.5px solid rgba(255,255,255,0.12)"
      : "1.5px solid rgba(0,0,0,0.08)",
    background: theme === "dark" 
      ? "rgba(15, 23, 42, 0.4)" 
      : "rgba(248, 250, 252, 0.9)",
    color: theme === "dark" ? "#e6eef8" : "#0f172a",
    transition: "all 300ms ease",
    outline: "none",
    fontWeight: "500",
  };

  const checklistItemStyle = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    borderRadius: 10,
    background: theme === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
    border: theme === "dark"
      ? "1px solid rgba(255,255,255,0.07)"
      : "1px solid rgba(0,0,0,0.07)",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    color: theme === "dark" ? "#cbd5e1" : "#374151",
    userSelect: "none",
    flex: "1 1 calc(33% - 8px)",
    minWidth: 200,
  };

  const renderCheckboxGrid = (items) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
      {items.map((cb) => (
        <label key={cb.name} style={checklistItemStyle}>
          <input
            type="checkbox"
            checked={Boolean(formData[cb.name])}
            onChange={(e) => handleFieldChange(cb.name, e.target.checked)}
            style={{ width: 16, height: 16, accentColor: "#2e7cba" }}
          />
          {cb.label}
        </label>
      ))}
    </div>
  );

  // ─────────────────────────────────────────────
  // Render (portal so modal is mounted outside page DOM)
  // ─────────────────────────────────────────────
  if (typeof document === "undefined") return null;

  return ReactDOM.createPortal(
    <div style={styles.modal} onClick={handleModalClick}>
      <div style={styles.modalContent} >

        {/* Close button */}
        <button
          type="button"
          aria-label={t.close || 'Close'}
          onClick={onClose}
          style={styles.closeBtn}
          // onMouseEnter={(e) => {
          //   e.currentTarget.style.transform = 'translateY(-2px)';
          //   e.currentTarget.style.boxShadow = theme === 'dark' ? '0 10px 30px rgba(2,6,23,0.7)' : '0 10px 30px rgba(2,6,23,0.08)';
          //   e.currentTarget.style.background = theme === 'dark' ? 'rgba(16, 24, 40, 0.7)' : 'rgba(255,255,255,1)';
          // }}
          // onMouseLeave={(e) => {
          //   e.currentTarget.style.transform = 'translateY(0)';
          //   e.currentTarget.style.boxShadow = theme === 'dark' ? '0 6px 18px rgba(2,6,23,0.6)' : '0 8px 20px rgba(2,6,23,0.06)';
          //   e.currentTarget.style.background = theme === 'dark' ? 'rgba(13, 18, 28, 0.55)' : 'rgba(255,255,255,0.92)';
          // }}
        >
          <X size={18} color={theme === 'dark' ? '#e6eef8' : '#0f172a'} />
        </button>

        {/* Header */}
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>
            <UserPlus size={28} 
            // style={{ filter: "drop-shadow(0 2px 4px rgba(59,130,246,0.3))" }}
             />
            {t.addNewEmployee}
          </h2>
        </div>

        <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>

          {/* ── Basic Information ── */}
          <SectionTitle styles={styles}>{t.basicInformation}</SectionTitle>

          <FormRow styles={styles}>
            <FormField label={`${t.fullName} *`} error={formErrors.name} styles={styles}>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                style={{
                  ...inputStyle,
                  borderColor: formErrors.name ? "#ef4444" : undefined,
                }}
                placeholder={t.enterFullName}
                onMouseEnter={(e) => {
                  if (!formErrors.name) {
                    e.currentTarget.style.borderColor = theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!formErrors.name) {
                    e.currentTarget.style.borderColor = theme === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)";
                  }
                }}
              />
            </FormField>

            <FormField label={t.arabicName} styles={styles}>
              <input
                type="text"
                name="arabicName"
                value={formData.arabicName}
                onChange={handleInputChange}
                style={inputStyle}
                placeholder={isRtl ? "أدخل الاسم بالعربي" : "Enter Arabic name"}
                dir="rtl"
              />
            </FormField>
          </FormRow>

          <FormRow styles={styles}>
            <FormField label={t.emailAddress} error={formErrors.email} styles={styles}>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                style={{
                  ...inputStyle,
                  borderColor: formErrors.email ? "#ef4444" : undefined,
                }}
                placeholder={t.enterEmail}
              />
            </FormField>

            <FormField label={t.phoneNumber} error={formErrors.phone} styles={styles}>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                style={{
                  ...inputStyle,
                  borderColor: formErrors.phone ? "#ef4444" : undefined,
                }}
                placeholder={t.enterPhone}
              />
            </FormField>
          </FormRow>
          <FormRow styles={styles}>
            <p style={{ fontSize: 12.5, opacity: 0.65, margin: "-6px 0 4px" }}>
              {t.emailOrPhoneHint || "* Either an email address or a phone number is required."}
            </p>
          </FormRow>

          <FormRow styles={styles}>
            <FormField label={isRtl ? "إضافة كـ" : "Add As"} styles={styles}>
              <select
                name="createAccount"
                value={formData.createAccount ? "account" : "processing_only"}
                onChange={(e) => handleFieldChange("createAccount", e.target.value === "account")}
                style={inputStyle}
              >
                <option value="account">
                  {isRtl ? "بحساب دخول (بريد/هاتف وكلمة مرور)" : "With Login Account (email/phone & password)"}
                </option>
                <option value="processing_only">
                  {isRtl ? "سجل الموظف فقط (بدون تسجيل دخول)" : "Employee Record Only (no login access)"}
                </option>
              </select>
            </FormField>
          </FormRow>
          <FormRow styles={styles}>
            <p style={{ fontSize: 12.5, opacity: 0.65, margin: "-6px 0 4px" }}>
              {isRtl
                ? "سجل الموظف فقط: يُحسب على حصة الموظفين لكن لا يُنشئ حساب دخول. بحساب دخول: يُحسب على حصتي الموظفين والحسابات معًا."
                : "Employee Record Only counts against the employee quota but never creates a login. With Login Account counts against both the employee and account quotas."}
            </p>
          </FormRow>

          <FormRow styles={styles}> 
            <FormField label={t.nationalId} styles={styles}>
              <input
                type="text"
                name="nationalId"
                value={formData.nationalId}
                onChange={handleInputChange}
                style={inputStyle}
                placeholder={t.enterNationalId}
              />
            </FormField>
          </FormRow>

          {/* ── Work Information ── */}
          <SectionTitle styles={styles}>{t.workInformation}</SectionTitle>

          <FormRow styles={styles}>
            <FormField label={t.workingCompany} error={formErrors.workingCompany} styles={styles}>
              <ReusableDropdown
                items={companyOptions}
                value={companySearchTerm}
                onChange={(val) => setCompanySearchTerm(val)}
                onSelect={(item) => {
                  const name = typeof item === "string" ? item : String(item);
                  if (name !== formData.workingCompany) {
                    // Departments depend on the selected company — reset them.
                    handleFieldChange("department", "");
                    handleFieldChange("departmentId", null);
                    setDepartmentSearchTerm("");
                  }
                  handleFieldChange("workingCompany", name);
                  setCompanySearchTerm(name);
                }}
                placeholder={
                  companiesLoading
                    ? t.loadingDepartments || "Loading..."
                    : t.selectWorkingCompany
                }
                disabled={companiesLoading}
                getItemKey={(it) => String(it)}
                getItemLabel={(it) => String(it)}
                theme={theme}
                language={language}
                inputStyle={inputStyle}
                maxItems={10}
              />
            </FormField>

            <FormField label={`${t.department} *`} error={formErrors.department} styles={styles}>
              <ReusableDropdown
                items={departmentItems}
                  value={departmentSearchTerm}
                onChange={(val) => setDepartmentSearchTerm(val)}
                onSelect={(item) => {
                  const label = typeof item === "string" ? item : item?.name || String(item);
                  handleFieldChange("department", label);
                  handleFieldChange(
                    "departmentId",
                    typeof item === "string" ? null : item?.id ?? null
                  );
                  setDepartmentSearchTerm(label);
                }}
                  placeholder={
                    !hasWorkingCompany
                      ? t.selectWorkingCompanyFirst
                      : departmentsLoading
                      ? (t.loadingDepartments || "Loading...")
                      : t.selectDepartment
                  }
                  disabled={departmentsLoading || !hasWorkingCompany}
                getItemKey={(it) => (typeof it === "string" ? it : it.id || String(it))}
                getItemLabel={(it) => (typeof it === "string" ? it : it.name || String(it))}
                theme={theme}
                language={language}
                inputStyle={inputStyle}
                maxItems={10}
                allowCreate={canAddDepartment}
                onCreate={handleCreateDepartment}
              />
            </FormField>
          </FormRow>

          <FormRow styles={styles}>
            <FormField label={`${t.position} *`} error={formErrors.position} styles={styles}>
              <input
                type="text"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                style={{
                  ...inputStyle,
                  borderColor: formErrors.position ? "#ef4444" : undefined,
                }}
                placeholder={t.enterPosition}
              />
            </FormField>

            <FormField label={t.contractCompany} styles={styles}>
              <input
                type="text"
                name="contractCompany"
                value={formData.contractCompany}
                onChange={handleInputChange}
                style={inputStyle}
                placeholder={t.enterContractCompany}
              />
            </FormField>
          </FormRow>

          {/* clientCode is auto-resolved; no manual input required */}
          {codeFormatHint && (
            <FormRow styles={styles}>
              <p style={{ fontSize: 12.5, opacity: 0.65, margin: "-6px 0 4px" }}>
                {isRtl
                  ? `سيتم إنشاء كود إنفينيتي تلقائيًا بالصيغة: ${codeFormatHint}`
                  : `Infinity Code will be auto-assigned in the format: ${codeFormatHint}`}
              </p>
            </FormRow>
          )}

          <FormRow styles={styles}>
            <FormField label={t.statusLabel} styles={styles}>
              <ReusableDropdown
                items={statusOptions}
                value={statusSearch}
                onChange={setStatusSearch}
                onSelect={(item) => {
                  handleFieldChange("status", item.key);
                  setStatusSearch(item.label);
                }}
                placeholder={t.selectStatus}
                getItemKey={(it) => it.key}
                getItemLabel={(it) => it.label}
                theme={theme}
                language={language}
                inputStyle={inputStyle}
                maxItems={10}
              />
            </FormField>

            <FormField label={isRtl ? "الراتب الإجمالي" : "Gross Salary"} styles={styles}>
              <input
                type="number"
                name="grossSalary"
                value={formData.grossSalary}
                onChange={(e) => handleSalaryFieldChange("grossSalary", e.target.value)}
                style={inputStyle}
                placeholder={isRtl ? "أدخل الراتب الإجمالي" : "Enter gross salary"}
                min="0"
                step="any"
              />
            </FormField>
          </FormRow>

          <FormRow styles={styles}>
            <FormField label={t.salaryLabel} styles={styles}>
              <input
                type="number"
                name="salary"
                value={formData.salary}
                onChange={(e) => handleSalaryFieldChange("salary", e.target.value)}
                style={inputStyle}
                placeholder={t.enterSalary}
                min="0"
                step="any"
              />
            </FormField>

            <FormField label={isRtl ? "رقم الحساب" : "Account Number"} styles={styles}>
              <input type="text" name="accountNumber" value={formData.accountNumber} onChange={handleInputChange} style={inputStyle} placeholder={isRtl ? "أدخل رقم الحساب" : "Enter account number"} />
            </FormField>

            <FormField label={isRtl ? "الموقع" : "Location"} styles={styles}>
              <input type="text" name="location" value={formData.location} onChange={handleInputChange} style={inputStyle} placeholder={isRtl ? "أدخل الموقع" : "Enter location"} />
            </FormField>
          </FormRow>

          {/* ── Personal Information ── */}
          <SectionTitle styles={styles}>{t.personalInformation}</SectionTitle>

          <FormRow styles={styles}>
            <FormField label={t.dateOfBirth} styles={styles}>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                style={inputStyle}
              />
            </FormField>

            <FormField label={t.gender} styles={styles}>
              <ReusableDropdown
                items={genderOptions}
                value={genderSearch}
                onChange={setGenderSearch}
                onSelect={(item) => {
                  handleFieldChange("gender", item.key);
                  setGenderSearch(item.label);
                }}
                placeholder={t.selectGender}
                getItemKey={(it) => it.key}
                getItemLabel={(it) => it.label}
                theme={theme}
                language={language}
                inputStyle={inputStyle}
                maxItems={10}
              />
            </FormField>
          </FormRow>

          <FormRow styles={styles}>
            <FormField label={t.maritalStatus} styles={styles}>
              <ReusableDropdown
                items={maritalOptions}
                value={maritalSearch}
                onChange={setMaritalSearch}
                onSelect={(item) => {
                  handleFieldChange("maritalStatus", item.key);
                  setMaritalSearch(item.label);
                }}
                placeholder={t.selectMaritalStatus}
                getItemKey={(it) => it.key}
                getItemLabel={(it) => it.label}
                theme={theme}
                language={language}
                inputStyle={inputStyle}
                maxItems={10}
              />
            </FormField>

            <FormField label={t.nationality} styles={styles}>
              <input
                type="text"
                name="nationality"
                value={formData.nationality}
                onChange={handleInputChange}
                style={inputStyle}
                placeholder={t.enterNationality}
              />
            </FormField>
          </FormRow>

          <FormRow styles={styles}>
            <FormField label={t.city} styles={styles}>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                style={inputStyle}
                placeholder={t.enterCity}
              />
            </FormField>

            <FormField label={t.country} styles={styles}>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                style={inputStyle}
                placeholder={t.enterCountry}
              />
            </FormField>
          </FormRow>

          <FormField label={t.address} styles={styles}>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              style={inputStyle}
              placeholder={t.enterAddress}
            />
          </FormField>

          {/* ── Contact Information ── */}
          <SectionTitle styles={styles}>{t.contactInformation}</SectionTitle>

          <FormRow styles={styles}>
            <FormField label={t.emergencyPhone} styles={styles}>
              <input
                type="tel"
                name="emergencyPhone"
                value={formData.emergencyPhone}
                onChange={handleInputChange}
                style={inputStyle}
                placeholder={isRtl ? "أدخل رقم الطوارئ" : "Enter emergency phone"}
              />
            </FormField>

            <FormField label={t.arabicAddress} styles={styles}>
              <input
                type="text"
                name="arabicAddress"
                value={formData.arabicAddress}
                onChange={handleInputChange}
                style={inputStyle}
                dir="rtl"
                placeholder={t.enterArabicAddress}
              />
            </FormField>
          </FormRow>

          <FormRow styles={styles}>
            <FormField label={t.hiringDate} styles={styles}>
              <input
                type="date"
                name="hiringDate"
                value={formData.hiringDate}
                onChange={handleInputChange}
                style={inputStyle}
              />
            </FormField>

            <FormField label={t.laborMode} styles={styles}>
              <ReusableDropdown
                items={laborModeOptions}
                value={laborModeSearch}
                onChange={setLaborModeSearch}
                onSelect={(item) => {
                  handleFieldChange("laborMode", item.key);
                  setLaborModeSearch(item.label);
                }}
                placeholder={isRtl ? "اختر نظام العمل" : "Select labor mode"}
                getItemKey={(it) => it.key}
                getItemLabel={(it) => it.label}
                theme={theme}
                language={language}
                inputStyle={inputStyle}
                maxItems={10}
              />
            </FormField>
          </FormRow>

          <FormRow styles={styles}>
            <FormField label={t.transportationAllowances} styles={styles}>
              <input
                type="number"
                name="transportationAllowances"
                value={formData.transportationAllowances}
                onChange={handleInputChange}
                style={inputStyle}
                placeholder={t.enterTransportation}
                min="0"
              />
            </FormField>

            <FormField label={t.variableSalary} styles={styles}>
              <input
                type="number"
                name="variableSalary"
                value={formData.variableSalary}
                onChange={handleInputChange}
                style={inputStyle}
                placeholder={t.enterVariableSalary}
                min="0"
              />
            </FormField>
          </FormRow>

          <FormRow styles={styles}>
            <FormField label={isRtl ? "المدير المباشر" : "First Line Manager"} styles={styles}>
              <ReusableDropdown
                items={managerOptions}
                value={managerSearchTerm}
                onChange={(val) => setManagerSearchTerm(val)}
                onSelect={(item) => {
                  const code = item?.clientCode ?? "";
                  const name = item?.sr ?? "";
                  handleFieldChange("firstLineManagerCode", code);
                  handleFieldChange("firstLineManagerName", name);
                  const label = code ? `${code} — ${name}` : name;
                  setManagerSearchTerm(label);
                }}
                placeholder={isRtl ? "اختر المدير المباشر..." : "Search manager by name or code..."}
                getItemKey={(it) => it.infinityCode || it.clientCode || it.sr}
                getItemLabel={(it) => {
                  const code = it.clientCode ? `${it.clientCode} — ` : "";
                  return isRtl
                    ? `${code}${it.arabicName || it.sr || ""}`
                    : `${code}${it.sr || it.arabicName || ""}`;
                }}
                theme={theme}
                language={language}
                inputStyle={inputStyle}
                maxItems={15}
              />
            </FormField>
          </FormRow>

          {/* ── Working Age Information ── */}
          <SectionTitle styles={styles}>{isRtl ? "خبرة العمل السابقة" : "Working Age"}</SectionTitle>

          <FormRow styles={styles}>
            <FormField label={isRtl ? "سنوات الخبرة" : "Working Age (Years)"} styles={styles}>
              <input
                type="number"
                name="workingAgeYears"
                value={formData.workingAgeYears}
                onChange={handleInputChange}
                style={inputStyle}
                placeholder={isRtl ? "عدد السنوات" : "Years"}
                min="0"
              />
            </FormField>

            <FormField label={isRtl ? "أشهر الخبرة" : "Working Age (Months)"} styles={styles}>
              <input
                type="number"
                name="workingAgeMonths"
                value={formData.workingAgeMonths}
                onChange={handleInputChange}
                style={inputStyle}
                placeholder={isRtl ? "عدد الأشهر" : "Months"}
                min="0"
                max="11"
              />
            </FormField>
          </FormRow>

          {/* ── Contract Information ── */}
          <SectionTitle styles={styles}>{t.contractInformation}</SectionTitle>

          <FormRow styles={styles}>
            <FormField label={t.contractDate} styles={styles}>
              <input type="date" name="contractDate" value={formData.contractDate} onChange={handleInputChange} style={inputStyle} />
            </FormField>

            <FormField label={t.termOfContract} styles={styles}>
              <input
                type="number"
                name="termOfContract"
                value={formData.termOfContract}
                onChange={handleInputChange}
                style={inputStyle}
                placeholder={isRtl ? "بالأشهر" : "In months"}
                min="0"
              />
            </FormField>
          </FormRow>

          <FormRow styles={styles}>
            <FormField label={t.contractExpiration} styles={styles}>
              <input type="date" name="contractExpiration" value={formData.contractExpiration} onChange={handleInputChange} style={inputStyle} />
            </FormField>

            <FormField label={t.lastContractDate} styles={styles}>
              <input type="date" name="lastContractDate" value={formData.lastContractDate} onChange={handleInputChange} style={inputStyle} />
            </FormField>
          </FormRow>

          <FormRow styles={styles}>
            <FormField label={t.lastContractTerm} styles={styles}>
              <ReusableDropdown
                items={lastContractTermOptions}
                value={lastContractTermSearch}
                onChange={setLastContractTermSearch}
                onSelect={(item) => {
                  handleFieldChange("lastContractTerm", item.key);
                  setLastContractTermSearch(item.label);
                }}
                placeholder={isRtl ? "اختر مدة آخر عقد" : "Select last contract term"}
                getItemKey={(it) => it.key}
                getItemLabel={(it) => it.label}
                theme={theme}
                language={language}
                inputStyle={inputStyle}
                maxItems={10}
              />
            </FormField>

            <FormField label={t.lastContractExpiration} styles={styles}>
              <input type="date" name="lastContractExpiration" value={formData.lastContractExpiration} onChange={handleInputChange} style={inputStyle} />
            </FormField>
          </FormRow>

          <FormRow styles={styles}>
            <FormField label={t.beRegularDate} styles={styles}>
              <input type="date" name="beRegularDate" value={formData.beRegularDate} onChange={handleInputChange} style={inputStyle} />
            </FormField>

            <FormField label={t.periodForBeingRegular} styles={styles}>
              <input type="text" name="periodForBeingRegular" value={formData.periodForBeingRegular} onChange={handleInputChange} style={inputStyle} placeholder={t.enterPeriodForRegular} />
            </FormField>
          </FormRow>

          <FormRow styles={styles}>
            <FormField label={isRtl ? "تاريخ الاستقالة" : "Resigned Date"} styles={styles}>
              <input type="date" name="resignedDate" value={formData.resignedDate} onChange={handleInputChange} style={inputStyle} />
            </FormField>

            <FormField label={isRtl ? "أسباب الاستقالة" : "Resign Reasons"} styles={styles}>
              <input type="text" name="resignReasons" value={formData.resignReasons} onChange={handleInputChange} style={inputStyle} placeholder={isRtl ? "أدخل أسباب الاستقالة" : "Enter resign reasons"} />
            </FormField>
          </FormRow>

          {renderCheckboxGrid([
            { name: "toBeRegular", label: isRtl ? "قابل للتثبيت" : "To Be Regular" },
          ])}

          {/* ── Education Information ── */}
          <SectionTitle styles={styles}>{t.educationInformation}</SectionTitle>

          <FormRow styles={styles}>
            <FormField label={t.graduateInstitution} styles={styles}>
              <input type="text" name="graduateInstitution" value={formData.graduateInstitution} onChange={handleInputChange} style={inputStyle} placeholder={isRtl ? "أدخل المؤسسة التعليمية" : "Enter institution"} />
            </FormField>

            <FormField label={t.major} styles={styles}>
              <input type="text" name="major" value={formData.major} onChange={handleInputChange} style={inputStyle} placeholder={isRtl ? "أدخل التخصص" : "Enter major"} />
            </FormField>
          </FormRow>

          {/* <FormRow styles={styles}>
            <FormField label={t.graduationYear} styles={styles}>
              <input type="number" name="graduationYear" value={formData.graduationYear} onChange={handleInputChange} style={inputStyle} placeholder={isRtl ? "مثال: 2018" : "e.g. 2018"} min="1950" max="2030" />
            </FormField>
          </FormRow> */}

          {/* ── Insurance Information ── */}
          <SectionTitle styles={styles}>{t.insuranceInformation}</SectionTitle>

          <FormRow styles={styles}>
            <FormField label={t.insuranceStartDate} styles={styles}>
              <input type="date" name="insuredBeginningDate" value={formData.insuredBeginningDate} onChange={handleInputChange} style={inputStyle} />
            </FormField>

            <FormField label={t.insuranceStatus} styles={styles}>
              <input type="text" name="insuranceStatus" value={formData.insuranceStatus} onChange={handleInputChange} style={inputStyle} placeholder={isRtl ? "حالة التأمين" : "Insurance status"} />
            </FormField>
          </FormRow>

          <FormRow styles={styles}>
            <FormField label={t.insuranceNumber} styles={styles}>
              <input type="text" name="insuranceNumber" value={formData.insuranceNumber} onChange={handleInputChange} style={inputStyle} placeholder={isRtl ? "أدخل رقم التأمين" : "Enter insurance number"} />
            </FormField>

            <FormField label={t.insuranceSalary} styles={styles}>
              <input type="number" name="insuranceSalary" value={formData.insuranceSalary} onChange={handleInputChange} style={inputStyle} placeholder={isRtl ? "راتب التأمين" : "Insurance salary"} min="0" />
            </FormField>
          </FormRow>

          <FormRow styles={styles}>
            <FormField label={t.monthsForInsurance} styles={styles}>
              <input type="number" name="monthsForInsurance" value={formData.monthsForInsurance} onChange={handleInputChange} style={inputStyle} placeholder={isRtl ? "أشهر التأمين" : "Months for insurance"} min="0" />
            </FormField>

            <FormField label={t.medicalInsuranceStage} styles={styles}>
              <input type="text" name="medicalInsuranceStage" value={formData.medicalInsuranceStage} onChange={handleInputChange} style={inputStyle} placeholder={t.enterMedicalStage} />
            </FormField>
          </FormRow>

          <FormRow styles={styles}>
            <FormField label={t.medicalNumber} styles={styles}>
              <input type="text" name="medicalNumber" value={formData.medicalNumber} onChange={handleInputChange} style={inputStyle} placeholder={t.enterMedicalNumber} />
            </FormField>
          </FormRow>

          {/* ── HR Information ── */}
          <SectionTitle styles={styles}>{t.hrInformation}</SectionTitle>

          <FormRow styles={styles}>
            <FormField label={t.idRegion} styles={styles}>
              <input type="text" name="idRegion" value={formData.idRegion} onChange={handleInputChange} style={inputStyle} placeholder={t.enterIdRegion} />
            </FormField>

            <FormField label={t.religion} styles={styles}>
              <input type="text" name="religion" value={formData.religion} onChange={handleInputChange} style={inputStyle} placeholder={t.enterReligion} />
            </FormField>
          </FormRow>

          <FormRow styles={styles}>
            <FormField label={t.militaryStatus} styles={styles}>
              <input type="text" name="militaryStatus" value={formData.militaryStatus} onChange={handleInputChange} style={inputStyle} placeholder={isRtl ? "الحالة العسكرية" : "Military status"} />
            </FormField>

            <FormField label={t.uniformSize} styles={styles}>
              <input type="text" name="uniformSize" value={formData.uniformSize} onChange={handleInputChange} style={inputStyle} placeholder={t.enterUniformSize} />
            </FormField>
          </FormRow>

          <FormRow styles={styles}>
            <FormField label={t.drivingLicenseDegree} styles={styles}>
              <input type="text" name="drivingLicenseDegree" value={formData.drivingLicenseDegree} onChange={handleInputChange} style={inputStyle} placeholder={t.enterLicenseDegree} />
            </FormField>

            <FormField label={t.drivingLicenseExpiry} styles={styles}>
              <input type="date" name="drivingLicenseExpiry" value={formData.drivingLicenseExpiry} onChange={handleInputChange} style={inputStyle} />
            </FormField>
          </FormRow>

          <FormRow styles={styles}>
            <FormField label={isRtl ? "تاريخ انتهاء الشهادة الصحية" : "Health Certificate Expiry Date"} styles={styles}>
              <input type="date" name="healthCertExpiryDate" value={formData.healthCertExpiryDate} onChange={handleInputChange} style={inputStyle} />
            </FormField>
          </FormRow>

          {renderCheckboxGrid([
            { name: "drivingLicenseStillValid", label: isRtl ? "رخصة القيادة سارية" : "Driving License Still Valid" },
            { name: "healthCertificateOB", label: isRtl ? "شهادة صحية (أوفيس بوي)" : "Health Certificate (Office Boys)" },
            { name: "healthCertStillValid", label: isRtl ? "الشهادة الصحية سارية" : "Health Certificate Still Valid" },
            { name: "delivered", label: isRtl ? "تم التسليم" : "Delivered" },
          ])}

          {/* ── Documents Checklist ── */}
          <SectionTitle styles={styles}>{isRtl ? "قائمة المستندات" : "Documents Checklist"}</SectionTitle>

          {renderCheckboxGrid([
            { name: "idCopy", label: isRtl ? "صورة البطاقة" : "ID Copy" },
            { name: "gradCertificateOriginal", label: isRtl ? "شهادة التخرج (أصل)" : "Graduation Certificate (Original)" },
            { name: "birthCertificateCopy", label: isRtl ? "شهادة الميلاد" : "Birth Certificate" },
            { name: "militaryServiceCertificate", label: isRtl ? "شهادة الخدمة العسكرية" : "Military Service Certificate" },
            { name: "laborLetterOriginal", label: isRtl ? "خطاب العمل (أصل)" : "Labor Letter (Original)" },
            { name: "noCrimeCertificate", label: isRtl ? "فيش جنائي" : "No Crime Certificate" },
            { name: "photosCount", label: isRtl ? "٦ صور شخصية" : "6 Photos" },
            { name: "formNo6Original", label: isRtl ? "نموذج ٦ (أصل)" : "Form No.6 (Original)" },
            { name: "medicalCheckForm111", label: isRtl ? "نموذج فحص طبي ١١١" : "Medical Check Form 111" },
            { name: "didMedicalCheck", label: isRtl ? "أجرى الفحص الطبي" : "Did Medical Check" },
            { name: "medicalReportOriginal", label: isRtl ? "تقرير طبي (أصل)" : "Medical Report (Original)" },
            { name: "lawyerCertificateCopy", label: isRtl ? "كارنيه المحاماة" : "Lawyer's Certificate" },
            { name: "driversLicenseCopy", label: isRtl ? "صورة رخصة القيادة" : "Driver's License Copy" },
            { name: "testEngineerCertificate", label: isRtl ? "شهادة مهندس" : "Test Engineer's Certificate" },
            { name: "accountantCertificate", label: isRtl ? "شهادة محاسب" : "Accountant Certificate" },
          ])}

          {/* ── Actions ── */}
          <div style={styles.modalActions}>
            <button
              type="button"
              style={styles.secondaryBtn}
              onClick={onClose}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = theme === "dark"
                  ? "rgba(51, 65, 85, 0.9)"
                  : "rgba(226, 232, 240, 1)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = theme === "dark"
                  ? "rgba(30, 41, 59, 0.8)"
                  : "rgba(241, 245, 249, 0.9)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              {t.cancel}
            </button>

            <button
              type="submit"
              style={{
                ...styles.primaryBtn,
                opacity: loading ? 0.8 : 1,
                cursor: loading ? "not-allowed" : "pointer",
              }}
              disabled={loading}
              // onMouseEnter={(e) => {
              //   if (!loading) {
              //     //e.currentTarget.style.boxShadow = "0 8px 25px rgba(59, 130, 246, 0.5)";
              //     e.currentTarget.style.transform = "translateY(-3px)";
              //   }
              // }}
              // onMouseLeave={(e) => {
              //   //e.currentTarget.style.boxShadow = "0 4px 15px rgba(59, 130, 246, 0.4)";
              //   e.currentTarget.style.transform = "translateY(0)";
              // }}
            >
              <UserPlus size={16} />
              {loading ? t.adding : t.addEmployee}
            </button>
          </div>

        </form>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .modal-content::-webkit-scrollbar {
          width: 6px;
        }
        .modal-content::-webkit-scrollbar-track {
          background: transparent;
        }
        .modal-content::-webkit-scrollbar-thumb {
          background: ${theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)"};
          border-radius: 3px;
        }
        .modal-content::-webkit-scrollbar-thumb:hover {
          background: ${theme === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)"};
        }
        input:focus {
          box-shadow: 0 0 0 3px ${theme === "dark" ? "rgba(59,130,246,0.15)" : "rgba(59,130,246,0.1)"} !important;
          border-color: ${theme === "dark" ? "rgba(96,165,250,0.5)" : "rgba(59,130,246,0.5)"} !important;
        }
        .modal-close {
          position: absolute;
          top: 12px;
          right: 12px;
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }
      `}</style>
    </div>,
    document.body
  );
};

export default AddEmployeeModal;